/* eslint-disable perfectionist/sort-enums */
// Base Event names for Amplitude
export enum AMPLITUDE_EVENT {
  // AI Assistant
  AI_ASSISTANT_ADMIN_PROMPT = 'Assistant Admin Prompt',
  AI_MCP_SERVER_TOGGLED = 'AI MCP Server Toggled',
  AI_MCP_DOCS_TEMPLATE_CLICKED = 'AI MCP Docs Template Clicked',
  AI_DROPDOWN_TOGGLED = 'AI Dropdown Toggled',
  AI_DROPDOWN_OPTION_CHANGED = 'AI Dropdown Option Changed',
  AI_LLMS_TXT_TOGGLED = 'AI LLMs.txt Toggled',

  // Billing
  BILLING_ADD_ON_CANCEL = 'AddOn: Cancel',
  BILLING_ADD_ON_SUCCESS = 'AddOn: Success',
  BILLING_FAILED_PAYMENT_VIEW = 'Failed Payment: View',
  BILLING_MANAGE_ADD_ONS_CHANGE = 'Manage AddOns: Change',
  BILLING_MANAGE_PLAN_CHANGE = 'Manage Plan: Change',
  BILLING_MANAGE_PLAN_VIEW = 'Manage Plan: View',
  BILLING_PAYMENT = 'Payment: Status',
  BILLING_SUBSCRIPTION_SUCCESS = 'Subscription: Success',

  // Branches
  BRANCH_REVIEW_LINTER_RUN = 'Branch Review: Linter Run',
  BRANCH_REVIEW_MARKED_READY = 'Branch Review: Marked Ready',

  // Docs Audit
  DOCS_AUDIT_RUN = 'Docs Audit Run',

  // Feature Feedback
  FEATURE_FEEDBACK = 'Feature Feedback',

  // Linter
  LINTER_RULE_SAVED = 'Linter Rule Saved',
  LINTER_RAN = 'Linter Ran',

  LOG_CAP_REACHED = 'Log Cap Reached',

  // Set Up API Logs (SDK)
  METRICS_SDK_CODE_COPY = 'Metrics Setup Copy',
  METRICS_SDK_CODE_SELECT = 'Metrics Setup Selection',
  METRICS_SDK_SUCCESS = 'Metrics Setup Success',
  METRICS_SDK_VIEW = 'Metrics Setup View',
  METRICS_SETUP_HELP_VIDEO = 'Metrics Setup Video: Play',
  METRICS_SLACK_CTA = 'Metrics Setup: Slack CTA',

  /**
   * Onboarding
   * Note: User Signup and Project Creation events are tracked server-side to avoid
   * dropping events from privacy-focused browser settings or ad blockers, the rest are tracked client-side
   */
  ONBOARDING_FINISH = 'Onboarding: Finish',
  ONBOARDING_UPLOAD_OAS_SUCCESS = 'Onboarding: Upload OAS Success',
  ONBOARDING_UPLOAD_OAS_SKIP = 'Onboarding: Upload OAS Skip',
  ONBOARDING_PROJECT_CREATE_SUCCESS = 'Onboarding: Project Created',
  ONBOARDING_USER_SIGNUP_SUCCESS = 'Onboarding: User Signup Success',
  ONBOARDING_VIEW = 'Onboarding: View',

  // Set Up API Keys (Webhooks)
  PD_WEBHOOK_CODE_COPY = 'PD Webhook Copy',
  PD_WEBHOOK_CODE_SELECT = 'PD Webhook Selection',
  PD_WEBHOOK_SUCCESS = 'PD Webhook Success',
  PD_WEBHOOK_TEST = 'PD Webhook Test',
  PD_WEBHOOK_VIEW = 'PD Webhook View',

  // PLP Click
  PLP_CLICK = 'PLP: Click',

  // Quickstart
  QS_CLICK = 'Quickstart: Click',
  QS_VIEW = 'Quickstart: View',

  // Auth
  USER_LOGGED_IN = 'Logged In',
  USER_LOGGED_OUT = 'Logged Out',
  USER_SIGNUP_SUCCESS = 'Signup Success',

  /**
   * Project Flow Events
   * Note: Some of these events are tracked server-side to capture direct API calls as well
   */

  // Frontend Tracked Events
  PROJECT_EDIT_STARTED = 'Project: Edit Started',
  PROJECT_REVIEW_STARTED = 'Project: Review Started',
  PROJECT_LAUNCHED_BUTTON = 'Project: Attempted Launch',
  PROJECT_LAUNCHED = 'Project: Launched, Plan Selected',
  PROJECT_LEFT_WITHOUT_SAVING = 'Project: Left Without Saving',
  PROJECT_AI_FEATURES_SAVED = 'Project: AI Features Saved',
  PROJECT_APPEARANCE_SAVED = 'Project: Appearance Saved',

  // Backend Tracked Events
  // Currently only tracking create and update events but can be easily expanded later
  PROJECT_GUIDE_NEW = 'Project: Guide New',
  PROJECT_GUIDE_SAVED = 'Project: Guide Saved',

  PROJECT_RECIPE_NEW = 'Project: Recipe New',
  PROJECT_RECIPE_SAVED = 'Project: Recipe Saved',

  PROJECT_API_DEFINITION_NEW = 'Project: API Definition Created',
  PROJECT_API_DEFINITION_SAVED = 'Project: API Definition Saved',

  PROJECT_REFERENCE_NEW = 'Project: Reference Page New',
  PROJECT_REFERENCE_SAVED = 'Project: Reference Page Saved',

  PROJECT_CHANGELOG_NEW = 'Project: Changelog New',
  PROJECT_CHANGELOG_SAVED = 'Project: Changelog Saved',

  PROJECT_CUSTOM_PAGE_NEW = 'Project: Custom Page New',
  PROJECT_CUSTOM_PAGE_SAVED = 'Project: Custom Page Saved',

  /**
   * Guide Events
   * These are guide events, used as 'markers' for guides to know when things are completed
   * Most of these are duplicates of some backend events, the guides SDK cannot listen in
   * to backend events so we have to resort to making new frontend events. Some of the events
   * are already frontend tracked, so we can reuse them, just marking them here for clarity.
   */
  // GUIDE_FINISHED_ADDING_APPEARANCE -> Using PROJECT_APPEARANCE_SAVED
  GUIDE_FINISHED_ADDING_CONTENT = 'Guide: Finished Adding Content',
  GUIDE_FINISHED_ADDING_API_DEFINITION = 'Guide: Finished Adding API Definition',
  // GUIDE_FINISHED_PRODUCT_LAUNCH -> Using BILLING_MANAGE_PLAN_CHANGE
}

// Type definitions for Amplitude event properties
export interface BaseDocumentEventProperties {
  action: string;
  page_type: string;
}

export interface BillingEventProperties {
  add_on?: string[] | string;
  add_on_removed?: string;
  frequency?: string;
  log_usage?: number | null;
  plan_type?: string;
  planCadence?: string;
  previousLogCap?: number;
  status?: string;
}

export interface BillingPaymentEventProperties {
  amount?: number;
  frequency?: string;
  plan_type?: string;
  status: 'Canceled' | 'Paid' | 'Past Due' | 'Unpaid';
}

export interface FeatureFeedbackEventProperties {
  feature_id: string;
  feature_reaction: string;
  feature_reaction_counts: number;
  feature_title: string;
}

export interface LogCapEventProperties {
  plan_type: string;
  previousLogCap: number;
}

export interface MetricsSDKEventProperties {
  language?: string;
  type: string;
}

export interface OnboardingEventProperties {
  type: string;
  upload?: Record<string, unknown>;
}

export interface OnboardingProjectCreateSuccessEventProperties {
  project: {
    id: string;
    sections: string[];
    name: string;
    subdomain: string;
  };
  source: string;
}

export interface OnboardingUploadSuccessEventProperties {
  file_name?: string;
  file_size?: number;
  is_legacy: boolean;
  upload_source: string;
}

export interface OnboardingSignupSuccessEventProperties {
  email: string;
  isInvite: boolean;
  source: string;
}

export interface PDWebhookEventProperties {
  language?: string;
  type: string;
}

export interface PLPClickEventProperties {
  state: string;
  type: string;
}

export interface AIFeaturesSavedEventProperties {
  ask_ai_dropdown?: boolean;
  ask_ai_options?: {
    ask_ai?: 'disabled' | 'enabled';
    chatgpt?: 'disabled' | 'enabled';
    claude?: 'disabled' | 'enabled';
    clipboard?: 'disabled' | 'enabled';
    copilot?: 'disabled' | 'enabled';
    view_as_markdown?: 'disabled' | 'enabled';
  };
  llm_txt?: boolean;
}

export interface APIDefinitionEventProperties extends BaseDocumentEventProperties {
  authentication_type?: string;
  authentication_type_changed?: boolean;
  new_authentication_type?: string;
  prev_authentication_type?: string;
  upload_source: string;
}

export interface ChangelogEventProperties extends BaseDocumentEventProperties {
  changelog_id: string;
  changelog_slug: string;
  changelog_title: string;
  changelog_type: string;
  is_public: boolean;
}

export interface CustomPageEventProperties extends BaseDocumentEventProperties {
  custom_page_id: string;
  custom_page_slug: string;
  custom_page_title: string;
}

export interface GuideEventProperties extends BaseDocumentEventProperties {
  guide_id: string;
  guide_slug: string;
  guide_title: string;
}

export interface RecipeEventProperties extends BaseDocumentEventProperties {
  recipe_id: string;
  recipe_slug: string;
  recipe_title: string;
}

export interface ReferenceEventProperties extends BaseDocumentEventProperties {
  reference_id: string;
  reference_slug: string;
  reference_title: string;
}

export interface QSEventProperties {
  step?: string;
  type: string;
}

export interface LogInLogOutEventProperties {
  email: string;
  method: string;
}

export interface AIAssistantAdminPromptEventProperties {
  agentResponse: string;
  agentTokens: number;
  model: string;
  pageUrl: string;
  totalTokensSent: number;
  user: string;
  userMessage: string;
  userTokens: number;
}

export interface AIMCPServerToggledEventProperties {
  enabled: boolean;
  mcpUrl: string;
  project: string;
  user: string;
}

export interface AIMCPDocsTemplateClickedEventProperties {
  mcpUrl: string;
  project: string;
  section: 'guides' | 'reference';
  user: string;
}

export interface AIDropdownToggledEventProperties {
  enabled: boolean;
  project: string;
  user: string;
}

export interface AIDropdownOptionChangedEventProperties {
  enabled: boolean;
  option: 'chatgpt' | 'claude' | 'clipboard' | 'copilot' | 'view_as_markdown';
  project: string;
  user: string;
}

export interface AILLMsTxtToggledEventProperties {
  enabled: boolean;
  llmsUrl: string;
  project: string;
  user: string;
}

// Event property mapping
// When adding a new event or new properties to an existing event,
// please update the EventPropertiesMap interface.
export interface EventPropertiesMap {
  [AMPLITUDE_EVENT.AI_ASSISTANT_ADMIN_PROMPT]: AIAssistantAdminPromptEventProperties;
  [AMPLITUDE_EVENT.AI_DROPDOWN_OPTION_CHANGED]: AIDropdownOptionChangedEventProperties;
  [AMPLITUDE_EVENT.AI_DROPDOWN_TOGGLED]: AIDropdownToggledEventProperties;
  [AMPLITUDE_EVENT.AI_LLMS_TXT_TOGGLED]: AILLMsTxtToggledEventProperties;
  [AMPLITUDE_EVENT.AI_MCP_DOCS_TEMPLATE_CLICKED]: AIMCPDocsTemplateClickedEventProperties;
  [AMPLITUDE_EVENT.AI_MCP_SERVER_TOGGLED]: AIMCPServerToggledEventProperties;
  [AMPLITUDE_EVENT.BILLING_ADD_ON_CANCEL]: BillingEventProperties;
  [AMPLITUDE_EVENT.BILLING_ADD_ON_SUCCESS]: BillingEventProperties;
  [AMPLITUDE_EVENT.BILLING_FAILED_PAYMENT_VIEW]: BillingPaymentEventProperties;
  [AMPLITUDE_EVENT.BILLING_MANAGE_ADD_ONS_CHANGE]: BillingEventProperties;
  [AMPLITUDE_EVENT.BILLING_MANAGE_PLAN_CHANGE]: BillingEventProperties;
  [AMPLITUDE_EVENT.BILLING_MANAGE_PLAN_VIEW]: Record<string, unknown>; // Contains no extra properties
  [AMPLITUDE_EVENT.BILLING_PAYMENT]: BillingPaymentEventProperties;
  [AMPLITUDE_EVENT.BILLING_SUBSCRIPTION_SUCCESS]: BillingEventProperties;
  [AMPLITUDE_EVENT.FEATURE_FEEDBACK]: FeatureFeedbackEventProperties;
  [AMPLITUDE_EVENT.GUIDE_FINISHED_ADDING_API_DEFINITION]: Record<string, unknown>; // Currently unused
  [AMPLITUDE_EVENT.GUIDE_FINISHED_ADDING_CONTENT]: Record<string, unknown>; // Currently unused
  [AMPLITUDE_EVENT.LOG_CAP_REACHED]: LogCapEventProperties;
  [AMPLITUDE_EVENT.METRICS_SDK_CODE_COPY]: MetricsSDKEventProperties;
  [AMPLITUDE_EVENT.METRICS_SDK_CODE_SELECT]: MetricsSDKEventProperties;
  [AMPLITUDE_EVENT.METRICS_SDK_SUCCESS]: MetricsSDKEventProperties;
  [AMPLITUDE_EVENT.METRICS_SDK_VIEW]: Record<string, unknown>; // Contains no extra properties
  [AMPLITUDE_EVENT.METRICS_SETUP_HELP_VIDEO]: Record<string, unknown>; // Contains no extra properties
  [AMPLITUDE_EVENT.METRICS_SLACK_CTA]: Record<string, unknown>; // Contains no extra properties
  [AMPLITUDE_EVENT.ONBOARDING_FINISH]: OnboardingEventProperties;
  [AMPLITUDE_EVENT.ONBOARDING_PROJECT_CREATE_SUCCESS]: OnboardingProjectCreateSuccessEventProperties;
  [AMPLITUDE_EVENT.ONBOARDING_UPLOAD_OAS_SKIP]: OnboardingEventProperties;
  [AMPLITUDE_EVENT.ONBOARDING_UPLOAD_OAS_SUCCESS]: OnboardingUploadSuccessEventProperties;
  [AMPLITUDE_EVENT.ONBOARDING_USER_SIGNUP_SUCCESS]: OnboardingSignupSuccessEventProperties;
  [AMPLITUDE_EVENT.ONBOARDING_VIEW]: OnboardingEventProperties;
  [AMPLITUDE_EVENT.PD_WEBHOOK_CODE_COPY]: PDWebhookEventProperties;
  [AMPLITUDE_EVENT.PD_WEBHOOK_CODE_SELECT]: PDWebhookEventProperties;
  [AMPLITUDE_EVENT.PD_WEBHOOK_SUCCESS]: PDWebhookEventProperties;
  [AMPLITUDE_EVENT.PD_WEBHOOK_TEST]: PDWebhookEventProperties;
  [AMPLITUDE_EVENT.PD_WEBHOOK_VIEW]: Record<string, unknown>; // Contains no extra properties
  [AMPLITUDE_EVENT.PLP_CLICK]: PLPClickEventProperties;
  [AMPLITUDE_EVENT.PROJECT_AI_FEATURES_SAVED]: AIFeaturesSavedEventProperties;
  [AMPLITUDE_EVENT.PROJECT_API_DEFINITION_NEW]: APIDefinitionEventProperties;
  [AMPLITUDE_EVENT.PROJECT_API_DEFINITION_SAVED]: APIDefinitionEventProperties;
  [AMPLITUDE_EVENT.PROJECT_APPEARANCE_SAVED]: Record<string, unknown>; // Currently unused
  [AMPLITUDE_EVENT.PROJECT_CHANGELOG_NEW]: ChangelogEventProperties;
  [AMPLITUDE_EVENT.PROJECT_CHANGELOG_SAVED]: ChangelogEventProperties;
  [AMPLITUDE_EVENT.PROJECT_CUSTOM_PAGE_NEW]: CustomPageEventProperties;
  [AMPLITUDE_EVENT.PROJECT_CUSTOM_PAGE_SAVED]: CustomPageEventProperties;
  [AMPLITUDE_EVENT.PROJECT_EDIT_STARTED]: Record<string, unknown>; // Contains no extra properties
  [AMPLITUDE_EVENT.PROJECT_GUIDE_NEW]: GuideEventProperties;
  [AMPLITUDE_EVENT.PROJECT_GUIDE_SAVED]: GuideEventProperties;
  [AMPLITUDE_EVENT.PROJECT_LAUNCHED]: Record<string, unknown>; // Contains no extra properties
  [AMPLITUDE_EVENT.PROJECT_LAUNCHED_BUTTON]: Record<string, unknown>; // Contains no extra properties
  [AMPLITUDE_EVENT.PROJECT_LEFT_WITHOUT_SAVING]: Record<string, unknown>; // Contains no extra properties
  [AMPLITUDE_EVENT.PROJECT_RECIPE_NEW]: RecipeEventProperties;
  [AMPLITUDE_EVENT.PROJECT_RECIPE_SAVED]: RecipeEventProperties;
  [AMPLITUDE_EVENT.PROJECT_REFERENCE_NEW]: ReferenceEventProperties;
  [AMPLITUDE_EVENT.PROJECT_REFERENCE_SAVED]: ReferenceEventProperties;
  [AMPLITUDE_EVENT.QS_CLICK]: QSEventProperties;
  [AMPLITUDE_EVENT.QS_VIEW]: QSEventProperties;
  [AMPLITUDE_EVENT.USER_LOGGED_IN]: LogInLogOutEventProperties;
  [AMPLITUDE_EVENT.USER_LOGGED_OUT]: LogInLogOutEventProperties;
  [AMPLITUDE_EVENT.USER_SIGNUP_SUCCESS]: Record<string, unknown>; // Currently unused
}

// Event properties for use in Amplitude events
export enum AMPLITUDE_EVENT_PROPERTY {
  // Feature Feedback
  FEATURE_ID = 'feature_id',
  FEATURE_REACTION = 'feature_reaction',
  FEATURE_REACTION_COUNTS = 'feature_reaction_counts',
  FEATURE_TITLE = 'feature_title',
  ONBOARDING_CREATE_PROJECT = 'Create Project',
  ONBOARDING_SIGNUP = 'Signup',
  ONBOARDING_UPLOAD_OAS = 'Upload OAS',
  PLP_BOOKMARK = 'PLP: Bookmark',
  QS_FINISH = 'Finish',
  QS_NEXT = 'Next',
  QS_OPEN_API_REFERENCE = 'Open API Reference',
  QS_OPEN_DISCUSSIONS = 'Open Discussions',
  QS_OPEN_GUIDES = 'Open Guides',
  QS_OPEN_LANDING_PAGE = 'Open Landing Page',
  QS_OPEN_MANUAL_EDITOR = 'Open Manual Editor',
  QS_OPEN_OAS_UPLOAD = 'Open OAS Upload',
  QS_SKIP = 'Skip',
  QS_STEP_DOCS = 'Step: Docs',
  QS_STEP_SETUP_API_KEYS = 'Step: Set Up API Keys',
  QS_STEP_SETUP_API_LOGS = 'Step: Set Up API Logs',
  QS_STEP_UPLOAD_API = 'Step: Upload API',
  QUICKSTART = 'Quickstart',
  SETUP = 'Setup',
}

// Source of user signup for Amplitude events
export enum SignupSource {
  // Direct project creation from Dash project list page (/signup/project/new)
  DashNewProject = 'Dash New Project',
  // Base signup flow (/signup)
  Signup = 'Signup',
}
